import type { SVGProps } from 'react';

/**
 * Icons vendored 1:1 from the Figma shipments designs (thin 1px-stroke style,
 * intentionally lighter than the Lucide defaults used elsewhere).
 */

/** Printer ("print label") action icon - 24px frame in the shipments table. */
export function PrinterIcon({ className, ...props }: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      {...props}
    >
      <path d="M6 18H4C3.46957 18 2.96086 17.7893 2.58579 17.4142C2.21071 17.0391 2 16.5304 2 16V11C2 10.4696 2.21071 9.96086 2.58579 9.58579C2.96086 9.21071 3.46957 9 4 9H20C20.5304 9 21.0391 9.21071 21.4142 9.58579C21.7893 9.96086 22 10.4696 22 11V16C22 16.5304 21.7893 17.0391 21.4142 17.4142C21.0391 17.7893 20.5304 18 20 18H18" />
      <path d="M6 9V3C6 2.73478 6.10536 2.48043 6.29289 2.29289C6.48043 2.10536 6.73478 2 7 2H17C17.2652 2 17.5196 2.10536 17.7071 2.29289C17.8946 2.48043 18 2.73478 18 3V9" />
      <path d="M17 14H7C6.44772 14 6 14.4477 6 15V21C6 21.5523 6.44772 22 7 22H17C17.5523 22 18 21.5523 18 21V15C18 14.4477 17.5523 14 17 14Z" />
    </svg>
  );
}

/** Truck ("shipment / tracking") action icon - 24px frame in the shipments table. */
export function TruckIcon({ className, ...props }: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      {...props}
    >
      <path d="M14 18V6C14 5.46957 13.7893 4.96086 13.4142 4.58579C13.0391 4.21071 12.5304 4 12 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V17C2 17.2652 2.10536 17.5196 2.29289 17.7071C2.48043 17.8946 2.73478 18 3 18H5" />
      <path d="M15 18H9" />
      <path d="M19 18H21C21.2652 18 21.5196 17.8946 21.7071 17.7071C21.8946 17.5196 22 17.2652 22 17V13.35C21.9996 13.1231 21.922 12.903 21.78 12.726L18.3 8.376C18.2065 8.25888 18.0878 8.16428 17.9528 8.0992C17.8178 8.03412 17.6699 8.00021 17.52 8H14" />
      <path d="M17 20C18.1046 20 19 19.1046 19 18C19 16.8954 18.1046 16 17 16C15.8954 16 15 16.8954 15 18C15 19.1046 15.8954 20 17 20Z" />
      <path d="M7 20C8.10457 20 9 19.1046 9 18C9 16.8954 8.10457 16 7 16C5.89543 16 5 16.8954 5 18C5 19.1046 5.89543 20 7 20Z" />
    </svg>
  );
}

/** Rounded "L" tree connector shown next to sub-row checkboxes. */
export function TreeBranchIcon({ className, ...props }: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="#CDCDCD"
      strokeWidth={1}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      {...props}
    >
      <path d="M2.66667 2.66667V7.33333C2.66667 8.04058 2.94762 8.71885 3.44772 9.21895C3.94781 9.71905 4.62609 10 5.33333 10H13.3333" />
    </svg>
  );
}

/** Expand/collapse chevron on multi-shipment rows (points up when expanded via rotate). */
export function ExpandChevronIcon({ className, ...props }: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="#818181"
      strokeWidth={1}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      {...props}
    >
      <path d="M6 9L12 15L18 9" />
    </svg>
  );
}

/** Back arrow used in the order view header - 20px frame. */
export function BackArrowIcon({ className, ...props }: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      {...props}
    >
      <path d="M5 6.66667L1.66667 10L5 13.3333" />
      <path d="M1.66667 10H18.3333" />
    </svg>
  );
}

/** Map-pin search icon on the order view "Parcel Locker" button - 22px frame. */
export function PointSearchIcon({ className, ...props }: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 22 22"
      fill="none"
      stroke="currentColor"
      strokeWidth={1}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      {...props}
    >
      <path d="M11.2273 20.1383C11.0942 20.1723 10.9552 20.1759 10.8205 20.1489C10.6858 20.122 10.5588 20.0651 10.4491 19.9824C8.74408 18.5103 3.66667 13.7436 3.66667 9.16667C3.66667 7.22175 4.43928 5.35648 5.81455 3.98122C7.18982 2.60595 9.05508 1.83333 11 1.83333C12.9449 1.83333 14.8102 2.60595 16.1855 3.98122C17.5607 5.35648 18.3333 7.22175 18.3333 9.16667C18.3333 9.55167 18.2976 9.93758 18.2307 10.3235" />
      <path d="M20.1667 20.1667L18.4433 18.4433" />
      <path d="M11 11.9167C12.5188 11.9167 13.75 10.6854 13.75 9.16667C13.75 7.64788 12.5188 6.41667 11 6.41667C9.48122 6.41667 8.25 7.64788 8.25 9.16667C8.25 10.6854 9.48122 11.9167 11 11.9167Z" />
      <path d="M16.5 19.25C18.0188 19.25 19.25 18.0188 19.25 16.5C19.25 14.9812 18.0188 13.75 16.5 13.75C14.9812 13.75 13.75 14.9812 13.75 16.5C13.75 18.0188 14.9812 19.25 16.5 19.25Z" />
    </svg>
  );
}
